import { Injectable } from '@angular/core';
import { LoggerService } from '@app/logger/logger.service';
import { ApiBaseService } from './api-base.service';
import {
  UserManual,
  ManualVideo,
  CreateUserManualPayload,
  UpdateUserManualPayload,
} from '@shared/models/entities/userManual.model';

const log = new LoggerService('USER MANUALS SERVICE');

/**
 * Manuales de usuario por rol. Recurso REST `userManuals`.
 *
 * Un registro por rol: el PDF del manual y la lista de videos por módulo.
 */
@Injectable({ providedIn: 'root' })
export class UserManualsService extends ApiBaseService<UserManual> {

  constructor() {
    super('userManuals');
    log.debug('Servicio de Manuales de Usuario inicializado');
  }

  /**
   * Lista los manuales. Con `role` trae solo el de ese rol (lo que ve un
   * usuario que no es Administrador Epione).
   */
  async list(role?: string | null): Promise<UserManual[]> {
    const condition: Record<string, unknown> = { deleted: false };
    if (role) condition['role'] = role;

    try {
      await this.search({ searchType: 'default', condition });
    } catch (err: any) {
      // 404 = todavía no hay ningún manual cargado. No es un error de verdad.
      if (err?.status === 404) {
        this.listData.set([]);
        return [];
      }
      throw err;
    }
    return (this.listData() ?? []).map(m => ({ ...m, videos: m.videos ?? [] }));
  }

  async createManual(payload: CreateUserManualPayload): Promise<UserManual | null> {
    const body: CreateUserManualPayload = {
      operationType: 'single',
      videos: [],
      ...payload,
    };
    const response = await this.create(body as unknown as UserManual);
    return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
  }

  async updateManual(payload: UpdateUserManualPayload): Promise<UserManual | null> {
    if (!payload.id) throw new Error('updateManual requiere id.');
    const body: UpdateUserManualPayload = { operationType: 'single', ...payload };
    const response = await this.update(body as unknown as UserManual);
    return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
  }

  /**
   * Devuelve el manual del rol, creándolo vacío si todavía no existe.
   * Sirve para que "subir el primer PDF" no tenga que distinguir alta de
   * edición en la vista.
   */
  async ensureForRole(role: string, roleName: string): Promise<UserManual | null> {
    const existing = await this.list(role);
    if (existing.length > 0) return existing[0];
    return this.createManual({ role, roleName });
  }

  /** Reemplaza la lista completa de videos del manual. */
  async saveVideos(id: string, videos: ManualVideo[]): Promise<UserManual | null> {
    return this.updateManual({ id, videos });
  }

  async softDeleteManual(id: string): Promise<UserManual | null> {
    return this.updateManual({ id, deleted: true });
  }
}
